import { useQueries } from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import { type Entity, formatTicker } from '@toggle/toggle';

import { useEntities } from '~/react-query-hooks/entities/useEntities';
import { QUERY_KEY_INSIGHT } from '~/react-query-hooks/insights/useInsight';
import { getInsight } from '~/services/insights/insights-service';
import { useFlags } from '~/stores/use-flags/useFlags';

import { resolveScenarioEntityTag } from '../scenario-item/formatScenarioDescription';
import type {
  ConversationInventory,
  FormattedConversationInventory,
} from '../services/inventory-service';

const getScenarioEntityTags = (
  inventory: ConversationInventory | undefined
): string[] => [
  ...new Set(
    (inventory?.analysis?.items ?? [])
      .map(resolveScenarioEntityTag)
      .filter((tag): tag is string => !!tag)
  ),
];

const entityNamesByTag = (entities: Entity[]): Record<string, string> =>
  Object.fromEntries(
    entities.map(entity => [entity.tag, entity.name_short || entity.name_full])
  );

export const useFormattedInventory = (
  inventory: ConversationInventory | undefined
): FormattedConversationInventory => {
  const isScenarioInventoryEnabled = useFlags(state =>
    state.getFeatureFlagState('scenario_inventory')
  );
  const insightIds = inventory?.insight?.items?.map(item => item.id) ?? [];

  const insightQueryResults = useQueries({
    queries: insightIds.map(id => ({
      queryKey: [QUERY_KEY_INSIGHT, id],
      queryFn: () => getInsight(id),
      enabled: insightIds.length > 0,
      staleTime: getTimeTillEndOfDayMs(),
    })),
  });

  const insightTitles = Object.fromEntries(
    insightIds.map((id, i) => {
      const res = insightQueryResults[i]?.data;
      const title = res?.data?.title ?? res?.data?.simple_title ?? id;
      return [id, title];
    })
  );

  const entityTags = inventory?.entity?.items?.map(i => i.tag) ?? [];
  const snakes = inventory?.timeseries?.items?.map(i => i.snake) ?? [];
  const financialStatements =
    inventory?.['financial-statement']?.items?.map(i => i.tag) ?? [];
  const scenarioEntityTags = isScenarioInventoryEnabled
    ? getScenarioEntityTags(inventory)
    : [];

  const snakesEntityTags = [
    ...new Set(snakes.map(snake => snake.split('.')[0])),
  ];
  const financialStatementsEntityTags = [
    ...new Set(
      financialStatements.map(
        financialStatement => financialStatement.split('.')[0]
      )
    ),
  ];
  const uniqueEntityTags = [...new Set(entityTags)];
  const { data: snakesData } = useEntities({ entityTags: snakesEntityTags });
  const { data: entitiesData } = useEntities({ entityTags: uniqueEntityTags });
  const { data: financialStatementsData } = useEntities({
    entityTags: financialStatementsEntityTags,
  });
  const { data: scenarioData } = useEntities({
    entityTags: scenarioEntityTags,
  });

  const formattedTickers = entitiesData.map(formatTicker);
  const formattedSnakes = snakesData.map(formatTicker);
  const formattedFinancialStatements =
    financialStatementsData.map(formatTicker);
  const scenarioEntityNames = entityNamesByTag(scenarioData);

  const restInventory = inventory ?? {};

  return {
    ...restInventory,
    ...(formattedTickers.length > 0 && {
      entity: {
        items: formattedTickers.map(ticker => ({ tag: ticker })),
      },
    }),
    ...(formattedSnakes.length > 0 && {
      timeseries: {
        items: formattedSnakes.map(snake => ({ snake })),
      },
    }),
    ...(formattedFinancialStatements.length > 0 && {
      ['financial-statement']: {
        items: formattedFinancialStatements.map(financialStatement => ({
          tag: financialStatement,
        })),
      },
    }),
    ...(Object.keys(insightTitles).length > 0 && { insightTitles }),
    ...(Object.keys(scenarioEntityNames).length > 0 && { scenarioEntityNames }),
  };
};
